import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Globe } from 'lucide-react';

// Tabs de aseguradora del directorio
const DIRECTORY_LINES = [
  { id: 'allianz', name: 'Allianz' },
  { id: 'insignia', name: 'Insignia Life' },
  { id: 'qualitas', name: 'Quálitas' },
];

// Mapeo de nombre de aseguradora (user.aseguradoras, de AgentDB o UserDB) → id de tab
const ASEGURADORA_TO_LINE_ID = {
  'Allianz': 'allianz',
  'Insignia Life': 'insignia',
  'Quálitas': 'qualitas',
};

export const DirectoryPage = () => {
  const { user, isAdmin } = useAuth();

  // Tabs por aseguradora — basado en user.aseguradoras[] del perfil (AgentDB/UserDB), NO en el contexto global
  const lineTabs = useMemo(() => {
    // isAdmin ya incluye master (ver AuthContext) → siempre ve los 3 tabs
    if (isAdmin) return DIRECTORY_LINES;

    const aseguradoras = user?.aseguradoras;
    if (!aseguradoras || aseguradoras.length === 0) return DIRECTORY_LINES; // fallback: sin config → todos

    const allowedIds = new Set(
      aseguradoras.map(a => ASEGURADORA_TO_LINE_ID[a]).filter(Boolean)
    );
    const own = DIRECTORY_LINES.filter(l => allowedIds.has(l.id));
    return own.length > 0 ? own : DIRECTORY_LINES; // fallback si ninguna aseguradora matchea
  }, [isAdmin, user?.aseguradoras]);

  const [activeTab, setActiveTab] = useState(lineTabs[0]?.id || '');

  useEffect(() => {
    if (lineTabs.length > 0 && !lineTabs.some(l => l.id === activeTab)) {
      setActiveTab(lineTabs[0].id);
    }
  }, [lineTabs]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Directorio</h1>
        <p className="text-muted-foreground">Contactos y sitios web por aseguradora</p>
      </div>

      {lineTabs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No tienes aseguradoras configuradas en tu perfil.
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap h-auto gap-1">
            {lineTabs.map(line => (
              <TabsTrigger key={line.id} value={line.id}>{line.name}</TabsTrigger>
            ))}
          </TabsList>

          {lineTabs.map(line => (
            <TabsContent key={line.id} value={line.id} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#1a365d]" />
                      Contactos
                    </CardTitle>
                    <CardDescription>Directorio de contactos de {line.name}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                      <Users className="h-10 w-10 mb-3 opacity-40" />
                      <p className="font-medium">Próximamente...</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-[#1a365d]" />
                      Sitios Web
                    </CardTitle>
                    <CardDescription>Enlaces útiles de {line.name}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                      <Globe className="h-10 w-10 mb-3 opacity-40" />
                      <p className="font-medium">Próximamente...</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};
